import { useMemo } from 'react';
import {
  DAILY_NAV_FIRM_DEFAULTS_COPY,
  DAILY_NAV_WINDOW_SESSIONS_FIELDS,
  DailyNavWindowSessionsKey,
  getDailyNavThresholdGroups,
  isValidThresholdPercent,
  isValidWindowSessions,
  WINDOW_SESSIONS_MAX,
  WINDOW_SESSIONS_MIN,
} from 'common/constants/dailyNav';
import { Alert, Box, Grid, Input, InputAdornment, Stack, Text } from 'components/ui';
import { FirmStepProps } from './FirmStep.props';

const WINDOW_DRAFT_KEYS: Record<DailyNavWindowSessionsKey, 'shortWindowSessions' | 'longWindowSessions'> = {
  short_window_sessions: 'shortWindowSessions',
  long_window_sessions: 'longWindowSessions',
};

const FirmThresholdsStep = ({ form, disabled }: FirmStepProps) => {
  const { draft, update, isWindowOrderValid } = form;

  // From what is typed, so editing a window relabels its thresholds at once.
  const thresholdGroups = useMemo(
    () => getDailyNavThresholdGroups(draft.shortWindowSessions, draft.longWindowSessions),
    [draft.shortWindowSessions, draft.longWindowSessions]
  );
  const bothWindowsInRange =
    isValidWindowSessions(draft.shortWindowSessions) && isValidWindowSessions(draft.longWindowSessions);

  return (
    <Stack spacing={4}>
      <div>
        <Text variant="h4" gutterBottom>
          How far back should each change look?
        </Text>
        <Text variant="body2" sx={{ mb: 2 }}>
          Each company shows how much its comps moved over a short and a long window, counted in trading days. The
          short window must be shorter than the long one; both between {WINDOW_SESSIONS_MIN} and {WINDOW_SESSIONS_MAX}.
        </Text>
        <Grid container spacing={2}>
          {DAILY_NAV_WINDOW_SESSIONS_FIELDS.map(field => {
            const value = draft[WINDOW_DRAFT_KEYS[field.key]];
            return (
              <Grid key={field.key} size={{ xs: 12, sm: 4 }}>
                <Input
                  fullWidth
                  size="small"
                  type="number"
                  id={`daily-nav-setup-window-sessions-${field.key}`}
                  label={field.label}
                  value={value}
                  onChange={event => update(WINDOW_DRAFT_KEYS[field.key], event.target.value)}
                  error={!isValidWindowSessions(value) || (bothWindowsInRange && !isWindowOrderValid)}
                  disabled={disabled}
                  slotProps={{ htmlInput: { min: WINDOW_SESSIONS_MIN, max: WINDOW_SESSIONS_MAX, step: 1 } }}
                />
              </Grid>
            );
          })}
        </Grid>
        {bothWindowsInRange && !isWindowOrderValid && (
          <Text variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            The short window must be less than the long window.
          </Text>
        )}
      </div>
      <div>
        <Text variant="h4" gutterBottom>
          When should a move be flagged?
        </Text>
        <Text variant="body2" sx={{ mb: 2 }}>
          A row is flagged for review when a change exceeds its threshold. {DAILY_NAV_FIRM_DEFAULTS_COPY}
        </Text>
        <Alert severity="info" sx={{ mb: 3 }}>
          Every threshold needs a value. Enter 0 to flag any movement at all.
        </Alert>
        {thresholdGroups.map(group => (
          <Box key={group.title} sx={{ mb: 3 }}>
            <Text variant="caption" fontWeight="bold" color="text.secondary">
              {group.title.toUpperCase()}
            </Text>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {group.fields.map(field => (
                <Grid key={field.key} size={{ xs: 12, sm: 4 }}>
                  <Input
                    fullWidth
                    size="small"
                    id={`daily-nav-setup-threshold-${field.key}`}
                    label={field.label}
                    value={draft.thresholds[field.key]}
                    onChange={event => update('thresholds', { ...draft.thresholds, [field.key]: event.target.value })}
                    error={!isValidThresholdPercent(draft.thresholds[field.key])}
                    disabled={disabled}
                    slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}
      </div>
    </Stack>
  );
};

export default FirmThresholdsStep;
